// src/components/LoginModal.jsx
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import {
  auth,
  db,
  signInWithPhone,
  verifyPhoneCode,
} from "@/lib/firebase";
import { RecaptchaVerifier } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { generateMemberId } from "@/lib/memberId";
import {
  handlePhoneInputChange,
  normalizePhoneNumber,
  isValidPhoneNumber,
} from "@/lib/phoneUtils";
import { ErrorCard } from "./NotificationCard";

/* ---------------- Portal root for the modal ---------------- */
function ModalRoot({ children }) {
  const [el] = useState(() => document.createElement("div"));
  useEffect(() => {
    el.setAttribute("id", "modal-root");
    document.body.appendChild(el);
    return () => document.body.removeChild(el);
  }, [el]);
  return createPortal(children, el);
}

function LoginModal({ open, onClose, onSuccess, onSwitchToSignup }) {
  // Email/password state
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [failCount, setFailCount] = useState(0);
  const [lastSubmit, setLastSubmit] = useState(0);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (!err) return;
    const t = setTimeout(() => setErr(""), 5000);
    return () => clearTimeout(t);
  }, [err]);

  // Phone login state
  const [phoneNumber, setPhoneNumber] = useState("+63");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [showPhoneForm, setShowPhoneForm] = useState(false);

  const idRef = useRef();
  const timerRef = useRef();

  /* ------------- Modal open/close side effects ------------- */
  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
      const t = setTimeout(() => idRef.current?.focus(), 100);
      timerRef.current = t;
    } else {
      document.body.classList.remove("modal-open");
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => {
      document.body.classList.remove("modal-open");
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open]);

  /* ------------- Invisible reCAPTCHA init (singleton) ------------- */
  useEffect(() => {
    if (showPhoneForm) initializeInvisibleRecaptcha();
  }, [showPhoneForm]);

  function initializeInvisibleRecaptcha() {
    if (window.__recaptcha) return window.__recaptcha;

    let box = document.getElementById("recaptcha-isolated-container");
    if (!box) {
      box = document.createElement("div");
      box.id = "recaptcha-isolated-container";
      box.style.position = "fixed";
      box.style.left = "-9999px";
      box.style.top = "0";
      box.style.width = "1px";
      box.style.height = "1px";
      box.style.zIndex = "2147483647";
      document.body.appendChild(box);
    }

    const v = new RecaptchaVerifier(auth, "recaptcha-isolated-container", {
      size: "invisible",
      callback: (token) => {},
      "expired-callback": () => console.warn("⚠️ reCAPTCHA expired"),
      "error-callback": (e) => console.error("❌ reCAPTCHA error:", e),
    });

    v.render().then((id) => console.log("reCAPTCHA widget id:", id));
    window.__recaptcha = v;
    return v;
  }

  /* ------------- Firestore helpers ------------- */

  /* ------------- Email/password sign-in ------------- */
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (busy) return;

    const now = Date.now();
    if (lockoutUntil && now < lockoutUntil) {
      setErr(`Too many failed attempts. Please wait ${Math.ceil((lockoutUntil - now) / 1000)} seconds.`);
      return;
    }
    if (now - lastSubmit < 2000) {
      setErr("Please wait a moment before trying again.");
      return;
    }

    setLastSubmit(now);
    setBusy(true);
    setErr("");

    try {
      const email = identifier.includes("@") ? identifier : null;
      if (!email) {
        setErr("Use your email for password sign-in, or use Phone.");
        setBusy(false);
        return;
      }

      // Try to sign in with Firebase Auth first
      const cred = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if user document exists in Firestore, create if not
      const userRef = doc(db, "users", cred.user.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        // Create user document if it doesn't exist
        const memberId = await generateMemberId(db);
        await setDoc(userRef, {
          uid: cred.user.uid,
          email: cred.user.email || "",
          displayName: cred.user.displayName || "",
          phoneNumber: cred.user.phoneNumber || "",
          memberId,
          roles: ["member"],
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        });
      } else {
        // Update last login time
        await updateDoc(userRef, {
          lastLoginAt: serverTimestamp(),
        });
      }

      setFailCount(0);
      setLockoutUntil(null);
      setIdentifier("");
      setPassword("");
      setPhoneNumber("+63");
      setVerificationCode("");
      setVerificationId("");

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Email sign-in error:", error);
      const fc = failCount + 1;
      setFailCount(fc);
      if (fc >= 5) {
        setLockoutUntil(Date.now() + 5 * 60 * 1000);
        setErr("Too many failed attempts. Please wait 5 minutes before trying again.");
        setIsLocked(true);
      } else {
        if (error.code === "auth/user-not-found") setErr("No account found with this email. Please sign up first.");
        else if (error.code === "auth/wrong-password") setErr("Incorrect password. Please try again.");
        else if (error.code === "auth/invalid-email") setErr("Invalid email format. Please check and try again.");
        else if (error.code === "auth/too-many-requests") setErr("Too many failed attempts. Please try again later.");
        else {
          setErr(error.message || "Sign-in failed. Please try again.");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  /* ------------- Google sign-in (server-side redirect) ------------- */
  const handleGoogleSignIn = () => {
    try {
      setBusy(true);
      setErr("");
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/api/auth/google/login?next=${next || "%2F"}`;
    } catch (error) {
      console.error("Google sign-in redirect error:", error);
      setErr(error.message || "Google sign-in failed. Please try again.");
      setBusy(false);
    }
  };

  /* ------------- Phone sign-in ------------- */
  const handlePhoneSignIn = async () => {
    try {
      setBusy(true);
      setErr("");

      const normalized = normalizePhoneNumber(phoneNumber);
      if (!isValidPhoneNumber(normalized)) {
        setErr("Please enter a valid PH mobile number (e.g., +63 9XX-XXX-XXXX).");
        return;
      }

      const verifier = window.__recaptcha || initializeInvisibleRecaptcha();
      const vid = await signInWithPhone(normalized, verifier);
      setVerificationId(vid);
    } catch (error) {
      console.error("Phone sign-in error:", error);
      if (error.code === "auth/too-many-requests") setErr("Too many attempts. Please try again later.");
      else if (error.code === "auth/invalid-phone-number") setErr("Invalid phone number format. Please check and try again.");
      else if (error.code === "auth/quota-exceeded") setErr("SMS quota exceeded. Please try again later.");
      else if (error.code === "auth/captcha-check-failed") setErr("reCAPTCHA verification failed. Please try again.");
      else setErr(error.message || "Failed to send verification code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setBusy(true);
      setErr("");
      if (!verificationCode.trim()) {
        setErr("Please enter the verification code.");
        return;
      }

      const cred = await verifyPhoneCode(verificationId, verificationCode);
      const user = cred.user;
      const uref = doc(db, "users", user.uid);
      const usnap = await getDoc(uref);

      if (!usnap.exists()) {
        const memberId = await generateMemberId(db);
        await setDoc(uref, {
          uid: user.uid,
          email: user.email || "",
          displayName: user.displayName || "",
          phoneNumber: user.phoneNumber || "",
          memberId,
          roles: ["member"],
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        });
      } else {
        await updateDoc(uref, { lastLoginAt: serverTimestamp() });
      }

      setPhoneNumber("+63");
      setVerificationCode("");
      setVerificationId("");
      setShowPhoneForm(false);

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Phone verification error:", error);
      if (error.code === "auth/invalid-verification-code") setErr("Invalid verification code. Please check and try again.");
      else if (error.code === "auth/code-expired") setErr("Verification code has expired. Please request a new one.");
      else if (error.code === "auth/invalid-verification-id") setErr("Verification session expired. Please request a new code.");
      else setErr(error.message || "Failed to verify code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <ModalRoot>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9998]" aria-hidden="true" />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Sign in</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {err && (
              <ErrorCard onClose={() => setErr("")}>
                <div className="text-sm whitespace-pre-line">{err || "Sign-in failed. Please try again."}</div>
              </ErrorCard>
            )}

            {/* Email/password */}
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email or Member ID</label>
                <input
                  ref={idRef}
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com or 202500001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    👁
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy || isLocked}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {busy ? "Signing in..." : "Sign in"}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-6">
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-sm text-gray-500">or</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
            </div>

            {/* Google Sign-In */}
            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={busy || isLocked}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 font-medium text-sm text-gray-700 shadow-sm transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </div>

            {/* Phone */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowPhoneForm(true)}
                disabled={busy || isLocked}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 font-medium text-sm"
              >
                📱 Continue with Phone
              </button>
            </div>

            {showPhoneForm && (
              <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-lg font-semibold mb-4">Phone Number Sign In</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(handlePhoneInputChange(e.target.value))}
                      placeholder="+63 950-468-6668"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {!verificationId ? (
                    <button
                      type="button"
                      onClick={handlePhoneSignIn}
                      disabled={busy}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                    >
                      {busy ? "Sending code..." : "Send Verification Code"}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="Enter 6-digit code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyCode}
                        disabled={busy}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                      >
                        {busy ? "Verifying..." : "Verify Code"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-6 text-sm">
              <a href="/reset" className="text-blue-600 hover:text-blue-800 underline">Forgot password?</a>
              <button
                onClick={() => { onClose(); onSwitchToSignup?.(); }}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                No account? Sign up
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalRoot>
  );
}

export default LoginModal;
